import React from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import SectionHeader from 'views/SectionHeader.js';
import { offerPricing } from 'files/LimitedOffer.js';
import { useServiceability } from 'views/ServiceabilityProvider.js';

// The stakes, in authoritative numbers. Each stat is a card with a simple
// graphic on a grey stage (like the product cards' image area) and the number
// and caption below. Every figure is cited to III / ISO data in the source line.

const AVERAGE_CLAIM = 15400;
const UNIT_OFF = '#D2D2D8'; // empty units / secondary bars, on white and on the grey stage

// 1 in 67: sixty-seven dots, one filled.
const DotsGraphic = () => (
    <div role={'img'} aria-label={'67 dots, one filled: 1 in 67 homes'} style={{ display: 'grid', gridTemplateColumns: 'repeat(17, 9px)', gap: 4 }}>
        {Array.from({ length: 67 }, (_, i) => (
            <span key={i} style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: i === 0 ? Appearance.colors.text() : UNIT_OFF }} />
        ))}
    </div>
);

// Horizontal bars on one scale starting at $0, each labeled with its value.
const CostGraphic = ({ rows, note, label }) => {
    const max = Math.max(...rows.map(r => r.value));
    const small = { fontFamily: Appearance.fonts.body, fontSize: 12.5, color: Appearance.colors.subText() };
    return (
        <div role={'img'} aria-label={label} style={{ width: '100%', maxWidth: 260, display: 'grid', gap: 14 }}>
            {rows.map((r, i) => (
                <div key={i} style={{ display: 'grid', gap: 5 }}>
                    <div style={{ ...small, display: 'flex', justifyContent: 'space-between', gap: 10, whiteSpace: 'nowrap' }}>
                        <span>{r.name}</span>
                        <b style={{ color: Appearance.colors.text(), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.text}</b>
                    </div>
                    <div style={{
                        height: 12,
                        width: `${Math.max(1.5, (r.value / max) * 100).toFixed(2)}%`,
                        borderRadius: '0 4px 4px 0',
                        backgroundColor: r.strong ? Appearance.colors.text() : UNIT_OFF,
                    }} />
                </div>
            ))}
            {note && <div style={small}>{note}</div>}
        </div>
    );
};

// ~1 in 4: a ring with a quarter filled, plus a two-item key.
const RingGraphic = () => {
    const r = 34;
    const c = 2 * Math.PI * r;
    const swatch = (color) => ({ width: 10, height: 10, borderRadius: 3, backgroundColor: color, display: 'inline-block' });
    const key = { display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' };
    return (
        <div role={'img'} aria-label={'Ring with one quarter filled: about 25% of insurance loss dollars come from water and freezing'} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <svg width={92} height={92} viewBox={'0 0 92 92'} aria-hidden={'true'}>
                <circle cx={46} cy={46} r={r} fill={'none'} stroke={UNIT_OFF} strokeWidth={14} />
                <circle cx={46} cy={46} r={r} fill={'none'} stroke={Appearance.colors.text()} strokeWidth={14}
                    strokeDasharray={`${(c * 0.25).toFixed(2)} ${c.toFixed(2)}`} transform={'rotate(-90 46 46)'} />
                <text x={46} y={51} textAnchor={'middle'} fontSize={15} fontWeight={600} fill={Appearance.colors.text()} fontFamily={Appearance.fonts.heading}>{'~25%'}</text>
            </svg>
            <div style={{ display: 'grid', gap: 6, fontFamily: Appearance.fonts.body, fontSize: 12.5, color: Appearance.colors.subText() }}>
                <span style={key}><i style={swatch(Appearance.colors.text())} />{'Water & freezing'}</span>
                <span style={key}><i style={swatch(UNIT_OFF)} />{'All other causes'}</span>
            </div>
        </div>
    );
};

const StatCard = ({ graphic, value, caption }) => (
    <div style={{ ...Appearance.styles.card(), boxShadow: Appearance.boxShadow.card, overflow: 'hidden' }}>
        <div style={{
            backgroundColor: Appearance.colors.offWhite,
            height: Utils.isMobile() ? 140 : 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '18px 22px',
        }}>
            {graphic}
        </div>
        <div style={{ padding: Utils.isMobile() ? '18px 22px 22px' : '22px 24px 26px', textAlign: 'center' }}>
            <div style={Appearance.textStyles.stat({ fontSize: Utils.isMobile() ? 36 : 46 })}>{value}</div>
            <div style={{ ...Appearance.textStyles.body({ fontSize: Utils.isMobile() ? 14 : 15 }), marginTop: Utils.isMobile() ? 8 : 10, textWrap: 'balance' }}>
                {caption}
            </div>
        </div>
    </div>
);

const RiskData = ({ products }) => {

    // $15,400 graphic: online-sales visitors see it against the Base Network
    // price they'd pay today (live, so it follows offers and price changes).
    // Everyone else, and anyone while prices load, sees the leak-vs-claim bars,
    // since pricing is never shown outside online-sales areas.
    const { canBuyOnline, offer } = useServiceability();
    const starter = (products || []).find(p => p.handle === 'starter-pack');
    const systemPrice = canBuyOnline && starter && parseFloat(starter.price) > 0
        ? offerPricing(starter, offer).price
        : 0;
    const multiple = systemPrice > 0 ? Math.round(AVERAGE_CLAIM / systemPrice) : 0;
    const costGraphic = systemPrice > 0 ? (
        <CostGraphic
            label={`The Base Network costs ${Utils.formatCurrency(systemPrice)}; the average claim is $15,400, about ${multiple} times as much`}
            rows={[
                { name: 'Base Network', text: Utils.formatCurrency(systemPrice).replace(/\.00$/, ''), value: systemPrice },
                { name: 'Average claim', text: '$15,400', value: AVERAGE_CLAIM, strong: true },
            ]}
            note={<>{'About '}<b style={{ color: Appearance.colors.text(), fontWeight: 600 }}>{`${multiple}×`}</b>{' the cost of the system'}</>} />
    ) : (
        <CostGraphic
            label={'A small leak found late can cost up to $10,000; the average claim is $15,400'}
            rows={[
                { name: 'Small leak, found late', text: 'up to $10,000', value: 10000 },
                { name: 'Average claim', text: '$15,400', value: AVERAGE_CLAIM, strong: true },
            ]} />
    );

    const culprits = [
        { icon: 'fas fa-tshirt', name: 'Washing machine', copy: 'Aging supply hoses crack or burst — often the #1 source.' },
        { icon: 'fas fa-utensils', name: 'Dishwasher', copy: 'Door seals and inlet valves fail unseen under the counter.' },
        { icon: 'fas fa-temperature-high', name: 'Water heater', copy: 'Tank corrosion floods fast — worst in attics and closets.' },
        { icon: 'fas fa-snowflake', name: 'Refrigerator', copy: 'Ice-maker lines loosen and leak behind the unit.' },
        { icon: 'fas fa-toilet', name: 'Toilets & sinks', copy: 'Overflows and supply-line failures let go without warning.' },
        { icon: 'fas fa-wind', name: 'A/C unit', copy: 'Clogged condensate drains quietly overflow for days.' },
    ];

    return (
        <section id={'risk'} style={{
            width: '100%',
            backgroundColor: Appearance.colors.white,
        }}>
            <div style={{
                maxWidth: Appearance.layout.maxWidth,
                margin: '0 auto',
                padding: Appearance.layout.sectionPadding(),
            }}>

                <SectionHeader
                    eyebrow={'Why early detection matters'}
                    title={'Water damage is one of the costliest things that can happen to a home.'}
                    subtitle={'It\'s among the most common homeowners insurance claims in the country — and most of it starts quietly, indoors, from something you use every day.'} />

                {/* Stats — graphic on a grey stage, number and caption below */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: Utils.isMobile() ? '1fr' : 'repeat(3, 1fr)',
                    gap: Utils.isMobile() ? 14 : 24,
                }}>
                    <StatCard graphic={<DotsGraphic />} value={'1 in 67'} caption={'homes file a water or freeze claim each year'} />
                    <StatCard graphic={costGraphic} value={'$15,400'} caption={'average payout on a water-damage claim'} />
                    <StatCard graphic={<RingGraphic />} value={'~1 in 4'} caption={'insurance loss dollars come from water and freezing'} />
                </div>

                {/* Source — linked so visitors can verify the figures */}
                <p style={{ ...Appearance.textStyles.caption({ color: Appearance.colors.lightGrey, textAlign: 'center' }), marginTop: 24 }}>
                    {'Source: '}
                    <a
                        href={'https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance'}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        style={{ color: Appearance.colors.primary(), textDecoration: 'none', fontWeight: 500 }}>
                        {'Insurance Information Institute'}
                    </a>
                    {' analysis of ISO homeowners insurance data, 2019–2023 averages.'}
                </p>

                {/* Culprits */}
                <div style={{ marginTop: Utils.isMobile() ? 56 : 88 }}>
                    <div style={{ textAlign: 'center', maxWidth: 725, margin: '0 auto', marginBottom: Utils.isMobile() ? 36 : 48 }}>
                        <h3 style={{ ...Appearance.textStyles.heading({ fontSize: Utils.isMobile() ? 24 : 32, textAlign: 'center' }), marginBottom: 14 }}>
                            {'It usually starts with an appliance'}
                        </h3>
                        <p style={Appearance.textStyles.sectionSubheading({ margin: '0 auto' })}>
                            {'A small, undetected leak can reach $6,000–$10,000 before you ever notice — and it almost always begins somewhere ordinary.'}
                        </p>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: Utils.isMobile() ? '1fr' : 'repeat(3, 1fr)',
                        gap: Utils.isMobile() ? 20 : 24,
                    }}>
                        {culprits.map((c, index) => (
                            <div key={index} style={{
                                ...Appearance.styles.card(),
                                boxShadow: Appearance.boxShadow.card,
                                padding: Utils.isMobile() ? 24 : 28,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                    <i className={c.icon} style={{ fontSize: 19, color: Appearance.colors.primary(), flexShrink: 0 }} />
                                    <h4 style={{ ...Appearance.textStyles.subHeading({ fontSize: 18 }), margin: 0 }}>
                                        {c.name}
                                    </h4>
                                </div>
                                <p style={Appearance.textStyles.body({ fontSize: 14 })}>
                                    {c.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default RiskData;
